// Первый экран для незарегистрированных: по макету «Лендинг» (21 окт). Шапка ведёт к разделам
// «Как это работает» и «Правила», вход и регистрация только в шапке и первом экране.
// Значения правил совпадают с services/engine/app/rules/defaults.py: при их изменении поправить здесь.
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/ui";
import "@/ui/landing.css";

const STARTERS: { name: string; meta: string; captain?: boolean }[][] = [
  [{ name: "Шестёркин", meta: "NYR · 5,9" }],
  [
    { name: "Макар", meta: "COL · 6,2" },
    { name: "Хьюз", meta: "VAN · 5,8" },
  ],
  [
    { name: "Кучеров", meta: "TBL · 6,5", captain: true },
    { name: "Ткачук", meta: "OTT · 6,4" },
    { name: "Овечкин", meta: "WSH · 5,1" },
  ],
];

interface SportRules {
  key: string;
  tab: string;
  rules: [string, string][];
  cols: string[];
  points: [string, ...string[]][];
  note: string;
}

const SPORT_RULES: SportRules[] = [
  {
    key: "hockey",
    tab: "Хоккей",
    rules: [
      [
        "Состав",
        "9 игроков: 6 в основе по схеме 1-2-3 (вратарь, 2 защитника, 3 нападающих) и 3 в запасе",
      ],
      ["Бюджет", "40 млн на состав, цена игрока по его очкам в прошлых сезонах"],
      ["Туры", "Неделя матчей. Состав меняется до дедлайна, потом фиксируется"],
      [
        "Замены",
        "1 новый игрок в основу и 1 в запас за тур, перестановки внутри состава бесплатны",
      ],
      ["Капитан", "Очки капитана ×2. Не вышел на лёд: за него играет запасной той же позиции"],
      ["Клубы", "Не больше 2 игроков из одного клуба"],
    ],
    cols: ["НАП", "ЗАЩ", "ВРТ"],
    points: [
      ["Гол", "4", "5", "—"],
      ["Передача", "3", "3", "—"],
      ["+/−", "±1", "±1", "—"],
      ["Каждые 4 броска", "1", "—", "—"],
      ["Каждые 3 блока", "—", "1", "—"],
      ["Победа · «сухой» матч", "—", "—", "4 · 4"],
      ["Каждые 5 сэйвов", "—", "—", "1"],
      ["Пропущенная шайба", "—", "—", "−1"],
      ["Сыграл матч", "1", "1", "1"],
      ["Каждые 2 мин штрафа", "−1", "−1", "−1"],
    ],
    note: "Броски, блоки и сэйвы считаются в каждом матче отдельно.",
  },
  {
    key: "basketball",
    tab: "Баскетбол",
    rules: [
      ["Состав", "8 игроков: 5 в основе (2 защитника, 2 форварда, центровой) и 3 в запасе"],
      ["Бюджет", "88 млн на состав, цена игрока по его очкам в прошлых сезонах"],
      ["Туры", "Неделя матчей. Состав меняется до дедлайна, потом фиксируется"],
      [
        "Замены",
        "1 новый игрок в основу и 1 в запас за тур, перестановки внутри состава бесплатны",
      ],
      [
        "Капитан",
        "Очки капитана ×1,5. Не вышел на площадку: за него играет первый сыгравший запасной",
      ],
      ["Клубы", "Не больше 2 игроков из одного клуба"],
    ],
    cols: ["Всем"],
    points: [
      ["Очко", "1"],
      ["Подбор", "1,2"],
      ["Передача", "1,5"],
      ["Перехват", "3"],
      ["Блок-шот", "3"],
      ["Потеря", "−1"],
      ["Дабл-дабл", "2"],
      ["Трипл-дабл", "5"],
      ["Сыграл матч", "1"],
    ],
    note: "Одинаково для всех позиций.",
  },
  {
    key: "football",
    tab: "Футбол",
    rules: [
      [
        "Состав",
        "15 игроков: 11 в основе по схеме на выбор (4-4-2, 4-3-3, 3-5-2, 3-4-3, 5-3-2, 5-4-1, 4-5-1) и 4 в запасе",
      ],
      ["Бюджет", "100 млн на состав, цена игрока по его очкам в прошлых сезонах"],
      ["Туры", "Тур чемпионата. Состав меняется до дедлайна, потом фиксируется"],
      [
        "Замены",
        "3 новых игрока в основу и 1 в запас за тур, перестановки внутри состава бесплатны",
      ],
      ["Капитан", "Очки капитана ×2. Не вышел на поле: за него играет запасной той же позиции"],
      ["Клубы", "Не больше 3 игроков из одного клуба"],
    ],
    cols: ["НАП", "ПЗ", "ЗАЩ", "ВРТ"],
    points: [
      ["Гол", "4", "5", "6", "6"],
      ["Передача", "3", "3", "3", "3"],
      ["«Сухой» матч", "—", "1", "4", "4"],
      ["Каждые 3 сэйва", "—", "—", "—", "1"],
      ["Каждые 2 пропущенных", "—", "—", "−1", "−1"],
      ["60+ минут · меньше 60", "2 · 1", "2 · 1", "2 · 1", "2 · 1"],
    ],
    note: "Всем: жёлтая −1, красная −3, автогол −2, незабитый пенальти −2, отбитый пенальти +5.",
  },
];

export function LandingPage() {
  return (
    <div className="landing">
      <section className="landing__hero">
        <nav className="landing__nav" aria-label="Навигация лендинга">
          <Link to="/" className="nav__brand">
            <Logo /> <span>Picked</span>
          </Link>
          <div className="landing__links">
            <a className="landing__link" href="#how">
              Как это работает
            </a>
            <a className="landing__link" href="#rules">
              Правила
            </a>
            <Link className="landing__link landing__link--strong" to="/login">
              Войти
            </Link>
            <Link className="landing__cta" to="/register">
              Создать аккаунт
            </Link>
          </div>
        </nav>
        <div className="landing__hero-body">
          <div className="landing__copy">
            <span className="landing__eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Фэнтези-спорт для своих
            </span>
            <h1>
              Твой состав.
              <br />
              Настоящие матчи.
              <br />
              Интерактивная таблица.
            </h1>
            <p className="landing__lead">
              Собери команду из профессиональных игроков спортивных лиг, уложись в бюджет и
              соревнуйся с друзьями или со всем сообществом. Очки начисляются по статистике матчей и
              сразу видны в онлайн-таблице.
            </p>
            <Link className="landing__start" to="/register">
              Начать бесплатно
            </Link>
          </div>
          <div className="landing__card-wrap" aria-hidden="true">
            <DemoCard />
            <div className="demo-toast">
              <span className="live-dot" />
              <span>
                <strong>Кучеров</strong> забил ·{" "}
                <span style={{ color: "var(--success)", fontWeight: 700 }}>+8 очков</span> капитану
                ×2
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="landing__section" id="how">
        <div className="landing__section-head">
          <h2>Как это работает</h2>
        </div>
        <div className="landing__steps">
          <Step
            n={1}
            title="Вступи в открытый турнир или создай свой"
            text="Играй в открытом турнире со всеми или создай закрытый для друзей. Пригласили? Вступай по коду."
          >
            <ShotBar />
            <ShotRow title="Открытая лига НХЛ" sub="Picked · 3 410 команд">
              <span className="shot__btn">+ Команда</span>
            </ShotRow>
            <ShotRow title="Открытая лига НБА" sub="Picked · 128 команд">
              <span className="shot__btn">+ Команда</span>
            </ShotRow>
            <div className="shot__row shot__row--col">
              <b>Офисный кубок</b>
              <small>закрытое · 12 команд</small>
              <span className="shot__code-line">
                <span className="shot__code">8MXU55BH</span>
                <span className="shot__btn shot__btn--line">Поделиться</span>
              </span>
            </div>
          </Step>
          <Step
            n={2}
            title="Собери состав"
            text="Уложись в бюджет, расставь игроков по позициям и выбери капитана: его очки удваиваются."
          >
            <div className="shot__row shot__row--thin">
              <small>
                Бюджет <b>27,0</b> из 40 млн
              </small>
              <small>9/9 · замены 1+1</small>
            </div>
            <div className="shot__rink">
              <div>
                <span>Шестёркин</span>
              </div>
              <div>
                <span>Макар</span>
                <span>Хьюз</span>
              </div>
              <div>
                <span className="shot__cap">Кучеров C</span>
                <span>Макдэвид</span>
                <span className="shot__add">+ НАП</span>
              </div>
            </div>
            <ShotRow title="Н. Маккиннон" sub="COL · НАП · 61 оч.">
              <span className="shot__btn">6,7 млн +</span>
            </ShotRow>
            <ShotRow title="И. Сорокин" sub="NYI · ВРТ · 48 оч.">
              <span className="shot__btn">6,1 млн +</span>
            </ShotRow>
            <ShotRow title="М. Шейфер" sub="NYI · ЗАЩ · 39 оч.">
              <span className="shot__btn">5,4 млн +</span>
            </ShotRow>
          </Step>
          <Step
            n={3}
            title="Следи за матчами и таблицей"
            text="Календарь тура с твоими игроками и турнирная таблица соревнования: очки и места обновляются после каждого матча."
          >
            <div className="shot__days">
              <span>ПН 20</span>
              <span className="on">ВТ 21</span>
              <span>СР 22</span>
              <span>ЧТ 23</span>
            </div>
            <div className="shot__row shot__row--col">
              <span className="shot__line">
                <b>COL 3 : 1 PIT</b>
                <small className="shot__live">2-й период</small>
              </span>
              <span className="shot__line">
                <small>Мэттьюс (К) · гол, передача</small>
                <b>+16</b>
              </span>
              <span className="shot__line">
                <small>Маккиннон · передача</small>
                <b>+5</b>
              </span>
            </div>
            <div className="shot__table">
              <span>
                <i>1</i>
                <b>Ice Breakers</b>
                <b>336</b>
              </span>
              <span>
                <i>2</i>
                <small>Puck Stop</small>
                <small>318</small>
              </span>
              <span>
                <i>3</i>
                <small>Slapshot</small>
                <small>297</small>
              </span>
            </div>
          </Step>
          <Step
            n={4}
            title="Обнови состав до дедлайна"
            text="Перед каждым туром можно сделать замены и сменить капитана. Не успел: состав перейдёт в новый тур как есть."
          >
            <div className="shot__row shot__row--alert">
              <small>Дедлайн тура 5 через 2 ч</small>
            </div>
            <div className="shot__row shot__row--thin">
              <small>Замены на тур</small>
              <small>
                <b>1 в основу · 1 в запас</b>
              </small>
            </div>
            <ShotRow title="Овечкин → Маккиннон" sub="в основу · +1,6 млн">
              <span className="shot__btn shot__btn--line">Отменить</span>
            </ShotRow>
            <ShotRow title="Капитан: Кучеров" sub="очки ×2" />
            <span className="shot__save">Сохранить состав</span>
          </Step>
        </div>
        <div className="landing__after">
          <span>Уведомления о дедлайне за сутки и за 2 часа</span>
          <span>Итоги матчей в Telegram</span>
          <span>История сезонов в профиле</span>
        </div>
      </section>

      <RulesSection />

      <footer className="landing__footer">
        <span>© Picked, 2026 · фэнтези-спорт</span>
      </footer>
    </div>
  );
}

function RulesSection() {
  const [active, setActive] = useState(SPORT_RULES[0]!.key);
  const sport = SPORT_RULES.find((s) => s.key === active) ?? SPORT_RULES[0]!;
  return (
    <section className="landing__section" id="rules">
      <div className="landing__section-head">
        <h2>Правила</h2>
      </div>
      <div className="landing__tabs" role="tablist" aria-label="Вид спорта">
        {SPORT_RULES.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === active}
            className={`landing__tab ${s.key === active ? "landing__tab--active" : ""}`}
            onClick={() => setActive(s.key)}
          >
            {s.tab}
          </button>
        ))}
      </div>
      <div className="landing__rules" role="tabpanel">
        <dl className="landing__rule-list">
          {sport.rules.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <div className="landing__points">
          <table>
            <thead>
              <tr>
                <th>Очки за</th>
                {sport.cols.map((c) => (
                  <th key={c} className="num">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sport.points.map(([label, ...values]) => (
                <tr key={label}>
                  <td>{label}</td>
                  {values.map((v, i) => (
                    <td key={i} className={`num ${v.startsWith("−") ? "neg" : ""}`}>
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p>{sport.note}</p>
        </div>
      </div>
    </section>
  );
}

function Step({
  n,
  title,
  text,
  children,
}: {
  n: number;
  title: string;
  text: string;
  children: ReactNode;
}) {
  return (
    <article className="landing__step">
      <span className="landing__step-n">{n}</span>
      <h3>{title}</h3>
      <p>{text}</p>
      <div className="shot" aria-hidden="true">
        {children}
      </div>
    </article>
  );
}

function ShotBar() {
  return (
    <div className="shot__bar">
      <i />
      <i />
      <i />
    </div>
  );
}

function ShotRow({
  title,
  sub,
  dim,
  children,
}: {
  title: string;
  sub: string;
  dim?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={`shot__row ${dim ? "shot__row--dim" : ""}`}>
      <span>
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      {children}
    </div>
  );
}

function DemoCard() {
  return (
    <div className="demo-card">
      <div className="demo-card__head">
        <span className="demo-card__title">Ice Breakers · тур 4</span>
        <span>
          Бюджет <strong style={{ color: "var(--text)" }}>27,0</strong> из 40 млн
        </span>
      </div>
      <div className="demo-rink">
        <div className="demo-rink__line" style={{ top: "33%" }} />
        <div className="demo-rink__line" style={{ top: "66%" }} />
        {STARTERS.map((row, i) => (
          <div key={i} className="demo-rink__row">
            {row.map((p) => (
              <div key={p.name} className="demo-slot">
                <div className="demo-slot__name">{p.name}</div>
                <div className="demo-slot__meta">{p.meta}</div>
                {p.captain && <span className="demo-slot__captain">C</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="demo-card__foot">
        <span>
          Замены на тур: <strong style={{ color: "var(--success)" }}>1 в основу · 1 в запас</strong>
        </span>
        <span>Дедлайн через 13 ч</span>
      </div>
    </div>
  );
}
